import { useState, useEffect, useRef, useCallback, useMemo, startTransition } from "react";
import { Search, Sparkles } from "lucide-react";
import { UniversalSearchSheet } from "@/components/search/UniversalSearchSheet";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { VirtualizedProductGrid } from "@/components/saves/VirtualizedProductGrid";
import { CollectionsList, CombinedCollectionFilter } from "@/components/collections/CollectionsList";
import { AddItemFAB, AddItemFABRef } from "@/components/saves/AddItemFAB";
import { AddItemSheet } from "@/components/saves/AddItemSheet";
import { AddToCollectionSheet } from "@/components/collections/AddToCollectionSheet";
import { CreateCollectionFAB } from "@/components/collections/CreateCollectionFAB";
import { AuthDialog } from "@/components/auth/AuthDialog";
import { SavesEmptyState } from "@/components/empty-states/SavesEmptyState";
import { EmptyState } from "@/components/empty-states/EmptyState";
import { ChatDemoSheet } from "@/components/activation/ChatDemoSheet";
import { MultiSelectActionBar } from "@/components/saves/MultiSelectActionBar";
import { SmartFilterSheet } from "@/components/saves/SmartFilterSheet";
import { SavesTabNav, ViewMode } from "@/components/saves/SavesTabNav";
import { AlertPills } from "@/components/saves/AlertPills";
import { AuthPromptCard } from "@/components/saves/AuthPromptCard";
import { DeleteConfirmDialog } from "@/components/saves/DeleteConfirmDialog";
import { ShareEducationBanner } from "@/components/saves/ShareEducationBanner";
import { SavesLoadingSkeleton } from "@/components/saves/SavesLoadingSkeleton";
import { HowToSaveSheet } from "@/components/activation/HowToSaveSheet";
import { ForYouTabContent } from "@/components/chat/ForYouTabContent";

import { useAuth } from "@/hooks/useAuth";
import { useSavedItems } from "@/hooks/useSavedItems";
import { useCollections, Collection } from "@/hooks/useCollections";
import { useCollectionFollows } from "@/hooks/useCollectionFollows";
import { useItemsInCollections } from "@/hooks/useItemsInCollections";
import { usePurchases } from "@/hooks/usePurchases";
import { useMultiSelect } from "@/hooks/useMultiSelect";
import { useSavesFilters } from "@/hooks/useSavesFilters";
import { useReportedItemIds } from "@/hooks/useItemDataIssues";
import { toast } from "sonner";
import { useHaptics } from "@/hooks/useHaptics";
import { useQueryClient } from "@tanstack/react-query";

export default function SavesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read tab from URL param and keep it synced
  const tabParam = searchParams.get("tab");
  const activeView: "saved" | "collections" | "for-you" = tabParam === "collections" ? "collections" : tabParam === "for-you" ? "for-you" : "saved";

  const [savedFilter, setSavedFilterRaw] = useState<string>("all");
  const [hideOrganised, setHideOrganisedRaw] = useState(false);
  const [collectionsFilter, setCollectionsFilterRaw] = useState<CombinedCollectionFilter>("all");

  // Wrap filter setters in startTransition so the grid re-render doesn't block the filter UI.
  const setSavedFilter = useCallback((v: string) => startTransition(() => setSavedFilterRaw(v)), []);
  const setHideOrganised = useCallback((v: boolean) => startTransition(() => setHideOrganisedRaw(v)), []);
  const setCollectionsFilter = useCallback((v: CombinedCollectionFilter) => startTransition(() => setCollectionsFilterRaw(v)), []);
  const [viewMode, setViewMode] = useState<ViewMode>("grid");

  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [bookmarkletUrl, setBookmarkletUrl] = useState<string | null>(null);
  const [collectionSheetItem, setCollectionSheetItem] = useState<{ id: string; name: string } | null>(null);

  const [showSearch, setShowSearch] = useState(false);
  const [showDemoSheet, setShowDemoSheet] = useState(false);
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [showAddItemSheet, setShowAddItemSheet] = useState(false);
  const [showBatchCollectionSheet, setShowBatchCollectionSheet] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showHowToSaveSheet, setShowHowToSaveSheet] = useState(false);

  const { user, loading: authLoading } = useAuth();
  const { items, isLoading, isFetching, deleteItem, deleteMultipleItems, createItem } = useSavedItems();
  const { collections } = useCollections();
  const { followedCollections } = useCollectionFollows();
  const { markMultipleAsPurchased } = usePurchases();
  const { itemsInCollections } = useItemsInCollections();
  const { data: reportedItemIds } = useReportedItemIds();

  // Multi-select state (must be before handleViewChange)
  const {
    isSelectMode,
    selectedItems,
    toggleItemSelection,
    selectAll,
    deselectAll,
    exitSelectMode,
    handleLongPressSelect,
    selectedCount,
  } = useMultiSelect(items);

  const handleViewChange = useCallback(
    (view: "saved" | "collections" | "for-you") => {
      exitSelectMode();
      if (view === "collections") {
        setSearchParams({ tab: "collections" });
      } else if (view === "for-you") {
        setSearchParams({ tab: "for-you" });
      } else {
        setSearchParams({});
      }
    },
    [setSearchParams, exitSelectMode],
  );

  // Compute collection type counts for dropdown
  const collectionTypeCounts = useMemo(
    () => ({
      private: collections.filter((c) => !c.is_collaborative && !c.is_public).length,
      collaborative: collections.filter((c) => c.is_collaborative).length,
      public: collections.filter((c) => c.is_public).length,
    }),
    [collections],
  );

  const haptics = useHaptics();
  const addItemFABRef = useRef<AddItemFABRef>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Handle navigation state for quick actions from bottom nav
  useEffect(() => {
    const state = location.state as { openAddSheet?: boolean } | null;
    if (state?.openAddSheet) {
      // Clear state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} });
      // Open add sheet
      setTimeout(() => addItemFABRef.current?.openSheet(), 100);
    }
  }, [location.state, navigate, location.pathname]);

  // Filtering logic
  const { priceDropCount, backInStockCount, allProducts, filteredProducts, getFilterLabel } = useSavesFilters(
    items,
    savedFilter,
    hideOrganised,
    itemsInCollections,
  );

  // Determine if empty state should be shown
  // CRITICAL: Force empty state when user is signed out, regardless of cached data
  // Also: Never show empty state while loading or fetching data
  const isSignedOut = !authLoading && !user;
  const isLoadingData = isLoading || (isFetching && items.length === 0);
  const showSavesEmptyState =
    !authLoading && !isLoadingData && (items.length === 0 || isSignedOut) && activeView === "saved";
  const showCollectionsEmptyState =
    !authLoading && activeView === "collections" && (collections.length === 0 || isSignedOut);
  const showEmptyState = showSavesEmptyState || showCollectionsEmptyState;

  const handleOpenAddItem = () => {
    if (showEmptyState) {
      setShowAddItemSheet(true);
    } else {
      addItemFABRef.current?.openSheet();
    }
  };

  const handleBatchMoveToCollection = () => {
    setShowBatchCollectionSheet(true);
  };

  const handleBatchMarkAsPurchased = async () => {
    if (!user || selectedCount === 0) return;
    haptics.success();

    const itemsData = Array.from(selectedItems).map((id) => {
      const item = items.find((i) => i.id === id);
      return {
        savedItemId: id,
        purchasePrice: item?.price,
        currency: item?.currency || "GBP",
        itemOwnerId: user.id,
      };
    });

    try {
      await markMultipleAsPurchased.mutateAsync(itemsData);
      toast.success(`${selectedCount} items added to Bought Items`);
      exitSelectMode();
    } catch {
      toast.error("Failed to mark items as purchased");
    }
  };

  const handleBatchDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleBatchTryOn = () => {
    if (selectedCount === 0) return;
    haptics.light();

    // Get selected item images
    const selectedItemImages = Array.from(selectedItems)
      .map((id) => items.find((i) => i.id === id))
      .filter((item) => item?.image_url)
      .map((item) => item!.image_url!);

    if (selectedItemImages.length === 0) {
      toast.error("No items with images selected");
      return;
    }

    // Navigate to try-on with selected items
    navigate("/app/try-on", {
      state: {
        selectedProductImages: selectedItemImages,
        autoGenerate: false,
      },
    });
    exitSelectMode();
  };

  const confirmBatchDelete = async () => {
    if (selectedCount === 0) return;
    haptics.warning();

    try {
      await deleteMultipleItems.mutateAsync(Array.from(selectedItems));
      toast.success(`${selectedCount} items removed`);
      exitSelectMode();
    } catch {
      toast.error("Failed to delete items");
    }
    setShowDeleteConfirm(false);
  };

  // Check for bookmarklet save URL on mount
  useEffect(() => {
    const saveUrl = searchParams.get("save");
    if (saveUrl) {
      setBookmarkletUrl(saveUrl);
      searchParams.delete("save");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Navigate to collection detail page
  const handleSelectCollection = (collection: Collection) => {
    navigate(`/app/collection/${collection.id}`);
  };

  // Memoized delete handler for virtualized grid
  const handleDeleteItem = useCallback(
    async (itemId: string, itemName: string) => {
      const itemToDelete = items.find((item) => item.id === itemId);

      try {
        await deleteItem.mutateAsync(itemId);
        toast(`"${itemName}" has been removed`, {
          action: itemToDelete
            ? {
                label: "Undo",
                onClick: async () => {
                  try {
                    await createItem.mutateAsync({
                      name: itemToDelete.name || undefined,
                      url: itemToDelete.url || undefined,
                      image_url: itemToDelete.image_url || undefined,
                      note: itemToDelete.note || undefined,
                      price: itemToDelete.price || undefined,
                      currency: itemToDelete.currency || undefined,
                      store: itemToDelete.store || undefined,
                      source_type: itemToDelete.source_type as "url" | "image" | "note" | "barcode",
                      ai_summary: itemToDelete.ai_summary || undefined,
                      ai_category: itemToDelete.ai_category || undefined,
                      ai_products: itemToDelete.ai_products || undefined,
                      ai_tags: itemToDelete.ai_tags || undefined,
                      platform: itemToDelete.platform || undefined,
                      ai_analyzed: itemToDelete.ai_analyzed || undefined,
                    });
                    toast.success(`"${itemName}" has been restored`);
                  } catch {
                    toast.error("Failed to restore item");
                  }
                },
              }
            : undefined,
        });
      } catch {
        toast.error("Failed to delete item");
      }
    },
    [items, deleteItem, createItem],
  );

  return (
    <div className="h-full flex flex-col bg-background overflow-hidden">
      <Header
        showAvatar
        showNotification
        noGradient={showEmptyState}
        rightContent={
          user ? (
            <button
              onClick={() => setShowSearch(true)}
              className="glass-bubble w-10 h-10"
              title="Search"
            >
              <Search className="w-[18px] h-[18px] header-glass-icon" strokeWidth={2} />
            </button>
          ) : null
        }
      />

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto overscroll-contain header-inset pb-nav tab-nav-glass-container">
        {!showSavesEmptyState && (
        <div className="sticky top-0 z-30 tab-nav-glass">
          <div className="px-5">
            <SavesTabNav
              activeView={activeView}
              onViewChange={handleViewChange}
              savedFilterLabel={getFilterLabel()}
              onOpenFilterSheet={() => setShowFilterSheet(true)}
              hasItems={items.length > 0}
              collectionsFilter={collectionsFilter}
              onCollectionsFilterChange={setCollectionsFilter}
              collectionsCount={collections.length}
              followingCount={followedCollections.length}
              collectionTypeCounts={collectionTypeCounts}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />
          </div>
        </div>
        )}

        <div className="px-5 mt-3">
        {/* Auth prompt if not logged in */}
        {!authLoading && !user && activeView === "saved" && <AuthPromptCard onClick={() => setShowAuthDialog(true)} />}

        {/* Alert Pills */}
        {activeView === "saved" && items.length > 0 && (
          <AlertPills priceDropCount={priceDropCount} backInStockCount={backInStockCount} activeFilter={savedFilter} onFilter={setSavedFilter} />
        )}

        {/* Saved Items View */}
        {activeView === "saved" && (
          <>
            {isLoadingData ? (
              <SavesLoadingSkeleton />
            ) : filteredProducts.length === 0 && savedFilter !== "all" ? (
              <EmptyState
                icon={Sparkles}
                title="No items in this filter"
                description="Try selecting a different filter or add more items"
                iconClassName="bg-muted/50"
              />
            ) : allProducts.length === 0 ? (
              <SavesEmptyState onAddItem={handleOpenAddItem} signedOut={!user} />
            ) : (
              <>
                <ShareEducationBanner itemCount={allProducts.length} onOpenHowToSave={() => setShowHowToSaveSheet(true)} />
                <VirtualizedProductGrid
                  products={filteredProducts}
                  onDelete={handleDeleteItem}
                  className="animate-fade-up"
                  selectionMode={isSelectMode}
                  selectedIds={selectedItems}
                  onToggleSelect={toggleItemSelection}
                  onLongPressSelect={handleLongPressSelect}
                  itemsInCollections={itemsInCollections}
                  viewMode={viewMode}
                  reportedItemIds={reportedItemIds}
                />
              </>
            )}
          </>
        )}

        {/* Collections View */}
        {activeView === "collections" && (
          <CollectionsList onSelectCollection={handleSelectCollection} filter={collectionsFilter} />
        )}

        {/* For You View */}
        {activeView === "for-you" && user && (
          <ForYouTabContent />
        )}
        </div>
      </div>

      {/* FAB */}
      {!showEmptyState && activeView === "collections" ? (
        <CreateCollectionFAB scrollRef={scrollContainerRef} hidden={isSelectMode} />
      ) : (
        !showEmptyState && (
          <AddItemFAB
            ref={addItemFABRef}
            initialUrl={bookmarkletUrl}
            onInitialUrlProcessed={() => setBookmarkletUrl(null)}
            showAttentionAnimation={false}
            scrollRef={scrollContainerRef}
            hidden={isSelectMode}
          />
        )
      )}

      {/* Dialogs and Sheets */}
      <AuthDialog open={showAuthDialog} onOpenChange={setShowAuthDialog} />

      <AddToCollectionSheet
        open={!!collectionSheetItem}
        onOpenChange={(open) => !open && setCollectionSheetItem(null)}
        savedItemId={collectionSheetItem?.id || ""}
        itemName={collectionSheetItem?.name}
      />

      <AddToCollectionSheet
        open={showBatchCollectionSheet}
        onOpenChange={(open) => {
          setShowBatchCollectionSheet(open);
          if (!open) exitSelectMode();
        }}
        savedItemIds={Array.from(selectedItems)}
      />

      <ChatDemoSheet open={showDemoSheet} onOpenChange={setShowDemoSheet} />

      {/* Multi-select action bar */}
      {isSelectMode && (
        <MultiSelectActionBar
          selectedCount={selectedCount}
          totalItems={items.length}
          onMoveToCollection={handleBatchMoveToCollection}
          onMarkAsPurchased={handleBatchMarkAsPurchased}
          onDelete={handleBatchDelete}
          onTryOn={handleBatchTryOn}
          onSelectAll={selectAll}
          onDeselectAll={deselectAll}
          onExit={exitSelectMode}
        />
      )}

      <DeleteConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        itemCount={selectedCount}
        onConfirm={confirmBatchDelete}
      />

      <UniversalSearchSheet open={showSearch} onOpenChange={setShowSearch} />

      <SmartFilterSheet
        open={showFilterSheet}
        onOpenChange={setShowFilterSheet}
        items={items}
        currentFilter={savedFilter}
        onFilterChange={setSavedFilter}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        hideOrganised={hideOrganised}
        onHideOrganisedChange={setHideOrganised}
        organisedCount={itemsInCollections.size}
      />

      {/* AddItemSheet for empty state */}
      {showEmptyState && (
        <AddItemSheet
          open={showAddItemSheet}
          onOpenChange={setShowAddItemSheet}
          initialUrl={bookmarkletUrl || undefined}
        />
      )}

      <HowToSaveSheet open={showHowToSaveSheet} onOpenChange={setShowHowToSaveSheet} />
    </div>
  );
}
